import { supabase } from '../lib/supabase';

const ERROR_MIEMBRO_NO_ENCONTRADO = 'No se encontró un miembro asociado a este usuario';
const ERROR_USUARIO_NO_AUTENTICADO = 'Usuario no autenticado';

export interface SolicitudRol {
  id: string;
  miembro_id: string;
  solicitante_miembro_id: string;
  rol_solicitado_id: string | null;
  tipo_solicitud: 'modificacion' | 'eliminacion';
  rol_a_eliminar_id: string | null;
  lumisial_id: string | null;
  diocesis_id: string | null;
  provincia_eclesiastica_id: string | null;
  motivo: string;
  estado: 'pendiente' | 'aprobada' | 'rechazada';
  comentarios_resolucion: string | null;
  fecha_solicitud: string;
  fecha_resolucion: string | null;
  resuelto_por_miembro_id: string | null;
}

export interface SolicitudRolWithDetails extends SolicitudRol {
  miembro: {
    id: string;
    nombres: string;
    apellidos: string;
    email: string;
    lumisial?: {
      id: string;
      nombre: string;
      diocesis?: { id: string; nombre: string; tipo: 'diocesis' | 'prefectura' };
      provincia_eclesiastica?: { id: string; nombre: string };
    };
  };
  solicitante_miembro: {
    id: string;
    nombres: string;
    apellidos: string;
    email: string;
  };
  rol_solicitado: {
    id: string;
    nombre: string;
    descripcion: string | null;
    nivel: number;
  } | null;
  rol_a_eliminar?: {
    id: string;
    nombre: string;
    descripcion: string | null;
    nivel: number;
  } | null;
  resuelto_por_miembro?: {
    id: string;
    nombres: string;
    apellidos: string;
  };
  lumisial?: {
    id: string;
    nombre: string;
  };
  diocesis?: {
    id: string;
    nombre: string;
  };
  provincia_eclesiastica?: {
    id: string;
    nombre: string;
  };
}

export interface CreateSolicitudRolData {
  miembro_id: string;
  rol_solicitado_id: string;
  lumisial_id?: string | null;
  diocesis_id?: string | null;
  provincia_eclesiastica_id?: string | null;
  motivo: string;
}

export interface ResolveSolicitudRolData {
  estado: 'aprobada' | 'rechazada';
  comentarios_resolucion: string;
}

const SOLICITUD_ROL_SELECT = `
  *,
  miembro:miembros!solicitudes_rol_miembro_id_fkey(
    id, nombres, apellidos, email,
    lumisial:lumisiales!lumisial_actual_id(
      id, nombre,
      diocesis:diocesis_id(id, nombre, tipo),
      provincia_eclesiastica:provincia_eclesiastica_id(id, nombre)
    )
  ),
  solicitante_miembro:miembros!role_requests_requester_miembro_id_fkey(id, nombres, apellidos, email),
  rol_solicitado:roles!role_requests_requested_role_id_fkey(id, nombre, descripcion, nivel),
  rol_a_eliminar:roles!solicitudes_rol_rol_a_eliminar_id_fkey(id, nombre, descripcion, nivel),
  resuelto_por_miembro:miembros!role_requests_approver_miembro_id_fkey(id, nombres, apellidos),
  lumisial:lumisiales(id, nombre),
  diocesis:diocesis(id, nombre),
  provincia_eclesiastica:provincias_eclesiasticas(id, nombre)
`;

export const solicitudRolService = {
  async getAll(): Promise<SolicitudRolWithDetails[]> {
    const { data, error } = await supabase
      .from('solicitudes_rol')
      .select(SOLICITUD_ROL_SELECT)
      .order('fecha_solicitud', { ascending: false });

    if (error) throw new Error('Error al obtener solicitudes de rol');
    return data as SolicitudRolWithDetails[];
  },

  async getPendientes(): Promise<SolicitudRolWithDetails[]> {
    const { data, error } = await supabase
      .from('solicitudes_rol')
      .select(SOLICITUD_ROL_SELECT)
      .eq('estado', 'pendiente')
      .order('fecha_solicitud', { ascending: false });

    if (error) throw new Error('Error al obtener solicitudes pendientes');
    return data as SolicitudRolWithDetails[];
  },

  async getHistorial(): Promise<SolicitudRolWithDetails[]> {
    const { data, error } = await supabase
      .from('solicitudes_rol')
      .select(SOLICITUD_ROL_SELECT)
      .in('estado', ['aprobada', 'rechazada'])
      .order('fecha_resolucion', { ascending: false });

    if (error) throw new Error('Error al obtener historial de solicitudes');
    return data as SolicitudRolWithDetails[];
  },

  async getMisSolicitudes(): Promise<SolicitudRolWithDetails[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error(ERROR_USUARIO_NO_AUTENTICADO);

    const { data, error } = await supabase
      .from('solicitudes_rol')
      .select(SOLICITUD_ROL_SELECT)
      .eq('solicitante_miembro_id', user.id)
      .order('fecha_solicitud', { ascending: false});

    if (error) throw new Error('Error al obtener mis solicitudes');
    return data as SolicitudRolWithDetails[];
  },

  async getById(id: string): Promise<SolicitudRolWithDetails> {
    const { data, error } = await supabase
      .from('solicitudes_rol')
      .select(SOLICITUD_ROL_SELECT)
      .eq('id', id)
      .single();

    if (error) throw new Error('Error al obtener solicitud');
    return data as SolicitudRolWithDetails;
  },

  async create(solicitudData: CreateSolicitudRolData): Promise<SolicitudRol> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error(ERROR_USUARIO_NO_AUTENTICADO);

    // Obtener el miembro actual (solicitante)
    const { data: miembroActual, error: miembroActualError } = await supabase
      .from('miembros')
      .select('id')
      .eq('user_id', user.id)
      .not('rol_id', 'is', null)
      .maybeSingle();

    if (miembroActualError) throw new Error('Error al obtener datos del solicitante');
    if (!miembroActual) throw new Error(ERROR_MIEMBRO_NO_ENCONTRADO);

    const { data: miembroData, error: miembroError } = await supabase
      .from('miembros')
      .select('rol_id')
      .eq('id', solicitudData.miembro_id)
      .maybeSingle();

    if (miembroError) throw new Error('Error al verificar miembro');
    if (!miembroData) throw new Error('Miembro no encontrado');

    if (miembroData.rol_id === solicitudData.rol_solicitado_id) {
      throw new Error('El miembro ya tiene el rol solicitado');
    }

    const { data: solicitudExistente, error: solicitudError } = await supabase
      .from('solicitudes_rol')
      .select('id')
      .eq('miembro_id', solicitudData.miembro_id)
      .eq('estado', 'pendiente')
      .limit(1)
      .maybeSingle();

    if (solicitudError && solicitudError.code !== 'PGRST116') {
      throw new Error('Error al verificar solicitudes existentes');
    }

    if (solicitudExistente) {
      throw new Error('Este miembro ya tiene una solicitud de rol pendiente');
    }

    const { data, error } = await supabase
      .from('solicitudes_rol')
      .insert({
        miembro_id: solicitudData.miembro_id,
        solicitante_miembro_id: miembroActual.id,
        rol_solicitado_id: solicitudData.rol_solicitado_id,
        tipo_solicitud: 'modificacion',
        lumisial_id: solicitudData.lumisial_id || null,
        diocesis_id: solicitudData.diocesis_id || null,
        provincia_eclesiastica_id: solicitudData.provincia_eclesiastica_id || null,
        motivo: solicitudData.motivo,
        estado: 'pendiente',
      })
      .select()
      .single();

    if (error) throw new Error('Error al crear solicitud de rol');
    return data as SolicitudRol;
  },

  async crearEliminacion(miembroId: string, motivo: string): Promise<SolicitudRol> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error(ERROR_USUARIO_NO_AUTENTICADO);

    // Obtener el miembro actual (solicitante)
    const { data: miembroActual, error: miembroActualError } = await supabase
      .from('miembros')
      .select('id')
      .eq('user_id', user.id)
      .not('rol_id', 'is', null)
      .maybeSingle();

    if (miembroActualError) throw new Error('Error al obtener datos del solicitante');
    if (!miembroActual) throw new Error(ERROR_MIEMBRO_NO_ENCONTRADO);

    // Rol actual y jerarquía del miembro objetivo (snapshot + columnas de ámbito para RLS)
    const { data: objetivo, error: objetivoError } = await supabase
      .from('miembros')
      .select('rol_id, lumisial_actual_id, lumisial:lumisiales!lumisial_actual_id(diocesis_id, provincia_eclesiastica_id)')
      .eq('id', miembroId)
      .maybeSingle();

    if (objetivoError) throw new Error('Error al verificar miembro');
    if (!objetivo) throw new Error('Miembro no encontrado');
    if (!objetivo.rol_id) throw new Error('El miembro no tiene un rol asignado');

    const { data: solicitudExistente, error: solicitudError } = await supabase
      .from('solicitudes_rol')
      .select('id')
      .eq('miembro_id', miembroId)
      .eq('estado', 'pendiente')
      .limit(1)
      .maybeSingle();

    if (solicitudError && solicitudError.code !== 'PGRST116') {
      throw new Error('Error al verificar solicitudes existentes');
    }

    if (solicitudExistente) {
      throw new Error('Este miembro ya tiene una solicitud de rol pendiente');
    }

    // Supabase infiere el join 1:1 como array — normalizar
    const lumisialData = Array.isArray(objetivo.lumisial) ? objetivo.lumisial[0] : objetivo.lumisial;

    const { data, error } = await supabase
      .from('solicitudes_rol')
      .insert({
        miembro_id: miembroId,
        solicitante_miembro_id: miembroActual.id,
        rol_solicitado_id: null,
        tipo_solicitud: 'eliminacion',
        rol_a_eliminar_id: objetivo.rol_id,
        lumisial_id: objetivo.lumisial_actual_id ?? null,
        diocesis_id: lumisialData?.diocesis_id ?? null,
        provincia_eclesiastica_id: lumisialData?.provincia_eclesiastica_id ?? null,
        motivo,
        estado: 'pendiente',
      })
      .select()
      .single();

    if (error) {
      if (error.code === '42501') {
        throw new Error('No tienes permisos para crear esta solicitud. Si el miembro no tiene lumisial asignado, solo un Admin Global puede eliminarle el rol.');
      }
      throw new Error('Error al crear solicitud de eliminación de rol');
    }
    return data as SolicitudRol;
  },

  async aprobar(id: string, comentarios: string): Promise<SolicitudRol> {
    const { data, error } = await supabase.rpc('resolver_solicitud_rol', {
      p_solicitud_id: id,
      p_aprobar: true,
      p_comentario: comentarios,
    });
    if (error) throw error;
    if (!data) throw new Error('La aprobación no devolvió la solicitud');
    return data as SolicitudRol;
  },

  async rechazar(id: string, comentarios: string): Promise<SolicitudRol> {
    const { data, error } = await supabase.rpc('resolver_solicitud_rol', {
      p_solicitud_id: id,
      p_aprobar: false,
      p_comentario: comentarios,
    });
    if (error) throw error;
    if (!data) throw new Error('El rechazo no devolvió la solicitud');
    return data as SolicitudRol;
  },

  async getCountPendientes(): Promise<number> {
    const { count, error } = await supabase
      .from('solicitudes_rol')
      .select('*', { count: 'exact', head: true })
      .eq('estado', 'pendiente');

    if (error) throw new Error('Error al contar solicitudes pendientes');
    return count || 0;
  },
};
